import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Elektronicky podpis smluv — typovany klient nad `/v1/contract-signatures`
 * (krok ES1, viz `packages/contexts/documents/src/public/plugin.ts`).
 *
 * Stejny vzor jako `contracts-api.ts` (T3) a `client-intake-api.ts` (KP1):
 * request body typy se ODVOZUJI z generovaneho OpenAPI kontraktu, DTO se
 * pisou rucne.
 *
 * Vsimni si, ze v requestu NENI `level` — schema ji nezna a server si ji
 * pocita z cesty podpisu (`levels.ts`). Kdyby tu bylo pole, makler by si
 * mohl poslat „kvalifikovany“ a REOS by zapsal podpis, ktery kvalifikovany
 * neni. Zde je to videt primo: `createSignatureSession` takove pole nestaci.
 *
 * Verejne cesty `/v1/public/signatures/*` tady NEJSOU zamerne — podpisuje
 * klient ve `apps/web-public` na `/podpis/<token>`.
 */

export type SignatureLevel = 'prosty' | 'zaruceny' | 'kvalifikovany';

export type SignatureRoute =
  'magic-link' | 'signi' | 'bankid-sign' | 'bankid-qsign' | 'papir-overeny';

export type SignerStatus =
  | 'ceka'
  /** ES2 bod 3: odkaz je vydany, ale nikomu neodesel — jde jen zkopirovat. */
  | 'k-odeslani'
  | 'odeslano'
  | 'otevreno'
  | 'souhlaseno'
  | 'podepsano'
  | 'odmito'
  | 'zneplatneno'
  | 'zastaveno';

export type ContractSignatureState =
  'nepodepsano' | 'ceka-na-strany' | 'cekaji-ostatni' | 'podepsano' | 'zastaveno' | 'neplatne';

export interface SignatureSignerDto {
  readonly id: string;
  readonly name: string;
  readonly partyKey: string;
  readonly partyIndex: number;
  readonly order: number;
  readonly status: SignerStatus;
  readonly signedAt: string | null;
  readonly openedAt: string | null;
  readonly remindedAt: string | null;
  readonly requiresSms: boolean;
  readonly smsVerified: boolean;
  /** Ma strana kontakt, kterym se odkaz da poslat? (ES2 bod 3) */
  readonly hasEmail: boolean;
  readonly hasPhone: boolean;
  /** `true` = jmeno strany jeste nikdo nevyplnil („čeká na údaje“, ES2 bod 2). */
  readonly awaitingData: boolean;
  readonly reasonCode: string | null;
  readonly labelKey: string;
}

export interface SealDto {
  readonly id: string;
  readonly sealHash: string;
  readonly sealVersion: number;
  readonly contentHash: string;
  readonly partyCount: number;
  readonly kind: 'interni' | 'poskytovatel';
  readonly provider: string | null;
  readonly sealedAt: string;
  readonly labelKey: string;
}

export interface SignatureSessionDto {
  readonly id: string;
  readonly contractId: string;
  readonly caseId: string | null;
  readonly versionNo: number;
  readonly contentHash: string;
  readonly level: SignatureLevel;
  readonly route: SignatureRoute;
  readonly status: 'vytvoreno' | 'rozeslano' | 'podepsano' | 'zastaveno' | 'neplatne';
  readonly state: ContractSignatureState;
  readonly stateLabelKey: string;
  readonly signers: readonly SignatureSignerDto[];
  readonly seal: SealDto | null;
  readonly sealed: boolean;
  readonly retainUntil: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

export interface IssuedSignatureLinkDto {
  readonly session: SignatureSessionDto;
  readonly partyId: string;
  readonly link: string;
  /** `true` = absolutni URL na verejny web; `false` = server nema `WEB_PUBLIC_URL` (ES2 bod 4). */
  readonly absolute: boolean;
  readonly delivered: boolean;
  /**
   * Krok MAIL1 bod 3 — PROČ se neodeslalo (`odesilani-vypnute`,
   * `hodinovy-strop`, …). `null` = odesláno, nebo kanál „jen zkopírovat".
   * Panel podle toho řekne, co se stalo, a nabídne „Kopírovat odkaz".
   */
  readonly notDeliveredReason?: string | null;
  readonly channel: 'email' | 'sms' | 'odkaz';
}

export interface SignaturePolicyDto {
  readonly level: SignatureLevel;
  readonly levelLabelKey: string;
  readonly route: SignatureRoute;
  readonly routeLabelKey: string;
  readonly reasonKey: string;
  readonly source: 'zakladni' | 'vzor' | 'kancelar';
  readonly floorApplied: boolean;
  readonly requiresBankIdentity: boolean;
  readonly electronic: boolean;
  readonly cadastralCapable: boolean;
  readonly blocking: readonly string[];
  readonly warnings: readonly string[];
}

export interface SignatureTimelineEventDto {
  readonly partyId: string | null;
  readonly kind: string;
  readonly actorUserId: string | null;
  readonly detail: Readonly<Record<string, unknown>>;
  readonly createdAt: string;
}

export interface EvidenceItemDto {
  readonly key: string;
  readonly ok: boolean;
  readonly labelKey: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface EvidencePackageDto {
  readonly contractId: string;
  readonly caseId: string | null;
  readonly versionNo: number;
  readonly contentHash: string;
  readonly level: SignatureLevel;
  readonly route: SignatureRoute;
  readonly electronic: boolean;
  readonly parties: readonly SignatureSignerDto[];
  readonly events: readonly {
    readonly kind: string;
    readonly at: string;
    readonly partyId: string | null;
    readonly actorId: string;
    readonly detail: Readonly<Record<string, unknown>>;
  }[];
  readonly seal: SealDto | null;
  readonly sealVerification: {
    readonly valid: boolean;
    readonly reasons: readonly string[];
  } | null;
  readonly pdfa: {
    readonly status: 'ocekavano' | 'dostupno';
    readonly fileId: string | null;
    readonly profile: 'pdfa-2b' | 'pdfa-3b';
  };
  readonly completeness: {
    readonly complete: boolean;
    readonly items: readonly EvidenceItemDto[];
    readonly missing: readonly string[];
  };
  readonly generatedAt: string;
  readonly retainUntil: string;
  readonly exportable: boolean;
}

export interface PrintPathDto {
  readonly html: string;
  readonly checklist: readonly {
    readonly kind: string;
    readonly labelKey: string;
    readonly responsible: string;
    readonly blocking: boolean;
    readonly automated: boolean;
  }[];
  readonly submitsToCadastre: false;
  readonly isPaper: boolean;
}

type CreateSessionBody =
  ApiPaths['/v1/contract-signatures']['post']['requestBody']['content']['application/json'];

export async function createSignatureSession(
  body: CreateSessionBody,
): Promise<{ session: SignatureSessionDto; firstLink: IssuedSignatureLinkDto | null }> {
  return (await unwrap(apiClient.POST('/v1/contract-signatures', { body }))) as unknown as {
    session: SignatureSessionDto;
    firstLink: IssuedSignatureLinkDto | null;
  };
}

export async function listSignatureSessions(filter: {
  readonly contractId?: string | undefined;
  readonly caseId?: string | undefined;
}): Promise<readonly SignatureSessionDto[]> {
  const query = {
    ...(filter.contractId !== undefined ? { contractId: filter.contractId } : {}),
    ...(filter.caseId !== undefined ? { caseId: filter.caseId } : {}),
  };
  return (await unwrap(
    apiClient.GET('/v1/contract-signatures', { params: { query } }),
  )) as unknown as readonly SignatureSessionDto[];
}

export async function getSignatureSession(id: string): Promise<SignatureSessionDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-signatures/{id}', { params: { path: { id } } }),
  )) as unknown as SignatureSessionDto;
}

export async function sendSignatureLink(
  id: string,
  channel: 'email' | 'sms' | 'odkaz',
): Promise<IssuedSignatureLinkDto | null> {
  const result = await unwrap(
    apiClient.POST('/v1/contract-signatures/{id}/send', {
      params: { path: { id } },
      body: { channel },
    }),
  );
  return (result ?? null) as IssuedSignatureLinkDto | null;
}

export async function remindSignature(id: string): Promise<{ readonly reminded: number }> {
  return (await unwrap(
    apiClient.POST('/v1/contract-signatures/{id}/remind', {
      params: { path: { id } },
      body: {},
    }),
  )) as unknown as { readonly reminded: number };
}

export async function stopSignatureSession(
  id: string,
  reason: string,
): Promise<SignatureSessionDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-signatures/{id}/stop', {
      params: { path: { id } },
      body: { reason },
    }),
  )) as unknown as SignatureSessionDto;
}

export async function signatureTimeline(id: string): Promise<readonly SignatureTimelineEventDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contract-signatures/{id}/timeline', { params: { path: { id } } }),
  )) as unknown as readonly SignatureTimelineEventDto[];
}

export async function evidencePackage(id: string): Promise<EvidencePackageDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-signatures/{id}/evidence', { params: { path: { id } } }),
  )) as unknown as EvidencePackageDto;
}

export async function printPath(id: string): Promise<PrintPathDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-signatures/{id}/print', { params: { path: { id } } }),
  )) as unknown as PrintPathDto;
}

export async function signaturePolicy(contractId: string): Promise<SignaturePolicyDto> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}/signature-policy', { params: { path: { id: contractId } } }),
  )) as unknown as SignaturePolicyDto;
}
